import { api } from '@/apis';
import { log } from '@/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addHours } from 'date-fns';

interface Source {
  collarNumber: string;
  name: string;
  teamId: number;
}

interface Shift {
  _id: string;
  team: string;
  source: Source;
  shiftName: string;
  callSign: string;
  phone?: string;
  remarks: string;
  startTime: Date;
  endTime: Date;
  activeSeconds: number;
  type: string;
  period: string;
  role?: string;
}

export function useShifts(date: Date, groupCodes: string[] = []) {
  const queryKey = ['shifts', date, groupCodes];
  const queryClient = useQueryClient();
  const query = useQuery<Shift[]>({
    queryKey,
    queryFn: async ({ signal }) => {
      const codesRecord = (await api
        .post('pipeline/groupHierarchies', {
          json: [
            { $match: { code: { $in: groupCodes } } },
            { $unwind: '$childGroups' },
            {
              $group: {
                _id: null,
                codes: { $addToSet: '$childGroups' },
              },
            },
          ],
        })
        .json()) as { codes: string[] }[];
      const aliasesRecord = (await api
        .post('pipeline/groups', {
          json: [
            { $match: { code: { $in: codesRecord[0].codes } } },
            { $unwind: '$aliases.GLOBAL_ROSTERING_SYSTEM' },
            {
              $group: {
                _id: null,
                aliases: { $addToSet: '$aliases.GLOBAL_ROSTERING_SYSTEM' },
              },
            },
          ],
        })
        .json()) as { aliases: string[] }[];

      const json = [
        {
          $match: {
            startTime: { $gte: addHours(date, 4), $lt: addHours(date, 28) },
            'source.teamId': { $in: aliasesRecord[0].aliases },
          },
        },
        {
          $lookup: {
            from: 'options',
            localField: 'shiftName',
            foreignField: 'values',
            pipeline: [{ $match: { name: 'shiftType' } }],
            as: 'types',
          },
        },
        {
          $project: {
            startTime: true,
            endTime: true,
            activeSeconds: true,
            team: true,
            source: true,
            callSign: true,
            remarks: true,
            shiftName: true,
            type: { $ifNull: [{ $first: '$types.value' }, ''] },
            period: {
              $switch: {
                branches: [
                  {
                    case: { $eq: ['$activeSeconds', 0] },
                    then: 'Off',
                  },
                  {
                    case: {
                      $and: [
                        {
                          $gte: [
                            {
                              $hour: {
                                date: '$startTime',
                                timezone: 'Europe/London',
                              },
                            },
                            4,
                          ],
                        },
                        {
                          $lt: [
                            {
                              $hour: {
                                date: '$startTime',
                                timezone: 'Europe/London',
                              },
                            },
                            11,
                          ],
                        },
                      ],
                    },
                    then: 'Day',
                  },
                  {
                    case: {
                      $and: [
                        {
                          $gte: [
                            {
                              $hour: {
                                date: '$startTime',
                                timezone: 'Europe/London',
                              },
                            },
                            11,
                          ],
                        },
                        {
                          $lt: [
                            {
                              $hour: {
                                date: '$startTime',
                                timezone: 'Europe/London',
                              },
                            },
                            19,
                          ],
                        },
                      ],
                    },
                    then: 'Late',
                  },
                  {
                    case: {
                      $or: [
                        {
                          $gte: [
                            {
                              $hour: {
                                date: '$startTime',
                                timezone: 'Europe/London',
                              },
                            },
                            19,
                          ],
                        },
                        {
                          $lt: [
                            {
                              $hour: {
                                date: '$startTime',
                                timezone: 'Europe/London',
                              },
                            },
                            4,
                          ],
                        },
                      ],
                    },
                    then: 'Night',
                  },
                ],
                default: '-',
              },
            },
          },
        },
        { $sort: { startTime: 1, shiftName: 1 } },
      ];

      log('Read', 'Shifts', { date, groups: groupCodes });

      const shifts = (await api
        .post('pipeline/personShifts', { json, signal })
        .json()) as Shift[];
      const collarNumbers = shifts.map((shift) => shift.source.collarNumber);
      const personData = (await api
        .post('pipeline/people', {
          json: [
            {
              $match: {
                collarNumber: { $in: collarNumbers },
              },
            },
            {
              $project: {
                collarNumber: true,
                role: true,
                mobileNumber: true,
              },
            },
          ],
          signal,
        })
        .json()) as {
        collarNumber: string;
        role: string;
        mobileNumber: string;
      }[];

      return shifts.map((shift) => {
        const person = personData.find(
          (person) => person.collarNumber === shift.source.collarNumber,
        );
        return {
          ...shift,
          role: person?.role,
          phone: person?.mobileNumber,
        };
      });
    },
    placeholderData: [],
    staleTime: 1000 * 60 * 60,
  });

  return {
    query,
    cancel: () => queryClient.cancelQueries({ queryKey }),
  };
}
